import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FileIndicator } from '../../common/FileIndicator';
import { Service, ServiceSelection, LiturgicalYear } from '../types/planningTypes';
import { MusicFilterState } from '../../Header/PerformanceTypeFilter';
import { fetchPlanningData } from '../../../services/planningService';
import { getLiturgicalClass } from '../ServiceCard/utils/dateUtils';
import { API_BASE_URL } from '../../../config/api';
import './DirectorView.css';
import { PlanningNoticeStrip } from '../PlanningNotices';
import { SongReminderBadge } from '../SongReminders';

const API_BASE = API_BASE_URL; // Use central config for API URL

interface DirectorViewProps {
  services: Service[];
  serviceSelections: ServiceSelection[];
  filterState: MusicFilterState;
  currentLiturgicalYear: LiturgicalYear;
  allLiturgicalYears: LiturgicalYear[];
  churchIds?: number[];
}

interface NotesPopover {
  selectionId: number;
  songTitle: string;
  currentNotes: string;
  x: number;
  y: number;
}

const DirectorView: React.FC<DirectorViewProps> = ({
  services,
  serviceSelections,
  filterState,
  currentLiturgicalYear,
  allLiturgicalYears,
  churchIds
}) => {
  const [weeksToShow, setWeeksToShow] = useState<number>(8);
  const [nextYearServices, setNextYearServices] = useState<Service[]>([]);
  const [nextYearSelections, setNextYearSelections] = useState<ServiceSelection[]>([]);
  const [isLoadingNextYear, setIsLoadingNextYear] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [notesPopover, setNotesPopover] = useState<NotesPopover | null>(null);
  const [editingNotes, setEditingNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Combine current and next year services
  const allServices = useMemo(() => {
    return [...services, ...nextYearServices].sort((a, b) =>
      new Date(a.liturgy_date).getTime() - new Date(b.liturgy_date).getTime()
    );
  }, [services, nextYearServices]);

  const allSelections = useMemo(() => {
    return [...serviceSelections, ...nextYearSelections];
  }, [serviceSelections, nextYearSelections]);

  // Fetch next year's data if date range extends beyond current year
  useEffect(() => {
    const fetchNextYearIfNeeded = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + (weeksToShow * 7));

      const lastServiceDate = services.length > 0
        ? new Date(services[services.length - 1].liturgy_date)
        : null;

      if (lastServiceDate && endDate > lastServiceDate) {
        const currentIndex = allLiturgicalYears.findIndex(y => y.id === currentLiturgicalYear.id);
        const nextYear = currentIndex < allLiturgicalYears.length - 1
          ? allLiturgicalYears[currentIndex + 1]
          : null;

        if (nextYear && !isLoadingNextYear) {
          setIsLoadingNextYear(true);
          try {
            const filters: any = { liturgicalYear: nextYear.id.toString() };
            if (churchIds && churchIds.length > 0) {
              filters.churchIds = churchIds;
            }
            const data = await fetchPlanningData(filters);
            setNextYearServices(data.planningData.services);
            setNextYearSelections(data.planningData.serviceSelections);
          } catch (error) {
            console.error('Error fetching next year data:', error);
          } finally {
            setIsLoadingNextYear(false);
          }
        }
      } else if (endDate <= (lastServiceDate || new Date())) {
        setNextYearServices([]);
        setNextYearSelections([]);
      }
    };

    fetchNextYearIfNeeded();
  }, [weeksToShow, services, currentLiturgicalYear, allLiturgicalYears, churchIds, isLoadingNextYear]);

  // Filter services by date range
  const filteredServices = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + (weeksToShow * 7));

    return allServices.filter(service => {
      const serviceDate = new Date(service.liturgy_date);
      return serviceDate >= today && serviceDate <= endDate;
    });
  }, [allServices, weeksToShow]);

  // Get selections for a service with filters applied
  const getSelectionsForService = useCallback((serviceId: number): ServiceSelection[] => {
    let selections = allSelections.filter(s => s.service_id === serviceId);

    if (filterState.selectedPerformanceTypes.length > 0) {
      selections = selections.filter(selection => {
        if (!selection.performance_type) return false;
        return filterState.selectedPerformanceTypes.some(typeId => {
          return selection.performance_type === String(typeId);
        });
      });
    }

    if (filterState.hideServiceMusic) {
      selections = selections.filter(s => !s.is_template_music);
    }

    return selections.sort((a, b) => a.order_number - b.order_number);
  }, [allSelections, filterState]);

  // Get all unique placements across filtered services
  const allPlacements = useMemo(() => {
    const placements: string[] = [];
    const seen = new Set<string>();

    filteredServices.forEach(service => {
      const selections = getSelectionsForService(service.service_id);
      selections.forEach(sel => {
        const placement = sel.placement?.trim() || 'Unknown';
        if (!seen.has(placement)) {
          seen.add(placement);
          placements.push(placement);
        }
      });
    });

    return placements;
  }, [filteredServices, getSelectionsForService]);

  // Handle PDF download
  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = today.toISOString().split('T')[0];

      const endDateObj = new Date(today);
      endDateObj.setDate(endDateObj.getDate() + (weeksToShow * 7));
      const endDate = endDateObj.toISOString().split('T')[0];

      let url = `${API_BASE}/planning/director-pdf?startDate=${startDate}&endDate=${endDate}`;
      if (churchIds && churchIds.length > 0) {
        url += `&churchIds=${churchIds.join(',')}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `Music-Schedule-${startDate}-to-${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(`Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Handle song click for notes editing
  const handleSongClick = (e: React.MouseEvent, selection: ServiceSelection) => {
    e.stopPropagation();
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    setNotesPopover({
      selectionId: selection.service_selection_id!,
      songTitle: selection.song_title,
      currentNotes: selection.director_notes || '',
      x: rect.left,
      y: rect.bottom + 5
    });
    setEditingNotes(selection.director_notes || '');
  };

  // Save notes (optional override for clear button)
  const handleSaveNotes = async (notesOverride?: string) => {
    if (!notesPopover) return;

    const notesToSave = notesOverride !== undefined ? notesOverride : editingNotes;

    setIsSavingNotes(true);
    try {
      // Find the full selection data
      const selection = allSelections.find(s => s.service_selection_id === notesPopover.selectionId);
      if (!selection) throw new Error('Selection not found');

      // Get auth token
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_BASE}/song-selections/update-selections`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          recordsToUpdate: [{
            service_selection_id: selection.service_selection_id,
            service_id: selection.service_id,
            song_id: selection.song_id,
            version_id: selection.version_id || null,
            placement: selection.placement,
            order_number: selection.order_number,
            performance_type: selection.performance_type || '',
            director_notes: notesToSave
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to save notes');

      // Update local state
      selection.director_notes = notesToSave;
      setNotesPopover(null);
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notesPopover && !(e.target as HTMLElement).closest('.notes-popover')) {
        setNotesPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notesPopover]);

  // Get badge info - show full performance type, use color based on category
  const getBadgeInfo = (performanceType: string | undefined) => {
    if (!performanceType || performanceType.toLowerCase().includes('congregation')) {
      return null;
    }
    const pt = performanceType.toLowerCase();
    const text = performanceType.toUpperCase();

    // Determine badge color class based on category (check more specific first)
    if (pt.includes('bell')) return { class: 'badge-bell', text };
    if (pt.includes('choir')) return { class: 'badge-choir', text };
    if (pt.includes('band')) return { class: 'badge-band', text };
    if (pt.includes('organ')) return { class: 'badge-organ', text };
    if (pt.includes('solo')) return { class: 'badge-solo', text };
    if (pt.includes('cantor')) return { class: 'badge-cantor', text };
    return { class: 'badge-other', text };
  };

  // Format short date
  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get display name without time
  const getDisplayName = (liturgyName: string | undefined) => {
    return liturgyName?.replace(/\s*-\s*\d{1,2}:\d{2}\s*(AM|PM)?/gi, '').trim() || 'Service';
  };

  return (
    <div className="director-view-table">
      {/* Controls */}
      <div className="director-controls">
        <label htmlFor="weeks-select">Show next: </label>
        <select
          id="weeks-select"
          value={weeksToShow}
          onChange={(e) => setWeeksToShow(Number(e.target.value))}
          className="weeks-selector"
        >
          <option value={4}>4 weeks</option>
          <option value={5}>5 weeks</option>
          <option value={6}>6 weeks</option>
          <option value={8}>8 weeks</option>
          <option value={10}>10 weeks</option>
          <option value={12}>12 weeks</option>
        </select>
        <button
          className="btn btn-sm btn-outline-secondary ms-3"
          onClick={() => window.print()}
        >
          Print
        </button>
        <button
          className="btn btn-sm btn-primary ms-2"
          onClick={handleDownloadPdf}
          disabled={isGeneratingPdf}
        >
          {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
        </button>
      </div>

      {/* Tables - 4 services per row */}
      {filteredServices.length === 0 ? (
        <div className="no-services">
          {isLoadingNextYear ? 'Loading...' : `No services found in the next ${weeksToShow} weeks.`}
        </div>
      ) : (
        <>
          {/* Chunk services into groups of 4 */}
          {Array.from({ length: Math.ceil(filteredServices.length / 4) }, (_, chunkIndex) => {
            const chunkServices = filteredServices.slice(chunkIndex * 4, (chunkIndex + 1) * 4);
            return (
              <div key={chunkIndex} className="table-wrapper" style={{ marginBottom: '24px' }}>
                <table className="director-table">
                  <thead>
                    <tr>
                      <th className="placement-header">Placement</th>
                      {chunkServices.map(service => (
                        <th
                          key={service.service_id}
                          className={`service-header season-${getLiturgicalClass(service.liturgical_season)}`}
                        >
                          <div className="service-date">{formatShortDate(service.liturgy_date)}</div>
                          <div className="service-name">{getDisplayName(service.liturgy_name)}</div>
                          <div className="service-time">{service.time || ''}</div>
                          <PlanningNoticeStrip serviceId={service.service_id} compact />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allPlacements.map(placement => (
                      <tr key={placement}>
                        <td className="placement-cell">{placement}</td>
                        {chunkServices.map(service => {
                          const selections = getSelectionsForService(service.service_id)
                            .filter(s => s.placement?.trim() === placement);

                          return (
                            <td key={service.service_id} className="song-cell">
                              {selections.length === 0 ? (
                                <span className="empty-cell">—</span>
                              ) : (
                                selections.map((sel, idx) => {
                                  const badge = getBadgeInfo(sel.performance_type);
                                  return (
                                    <div
                                      key={sel.service_selection_id || idx}
                                      className="song-item"
                                      onClick={(e) => handleSongClick(e, sel)}
                                      title="Click to add/edit notes"
                                    >
                                      <span className="song-title"><FileIndicator hasFile={sel.has_file} />{sel.song_title}</span>
                                      <SongReminderBadge songId={sel.song_id} title={sel.song_title} />
                                      {badge && (
                                        <span className={`song-badge ${badge.class}`}>{badge.text}</span>
                                      )}
                                      {sel.director_notes && (
                                        <div className="song-note">{sel.director_notes}</div>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      )}

      {/* Notes Popover */}
      {notesPopover && (
        <div
          className="notes-popover"
          style={{ left: notesPopover.x, top: notesPopover.y }}
        >
          <div className="notes-popover-header">
            Notes for: {notesPopover.songTitle}
          </div>
          <textarea
            value={editingNotes}
            onChange={(e) => setEditingNotes(e.target.value)}
            placeholder="e.g., oboe needed, extra rehearsal"
            rows={3}
            autoFocus
          />
          <div className="notes-popover-buttons">
            {notesPopover.currentNotes && (
              <button
                className="btn btn-sm btn-outline-danger me-auto"
                onClick={() => {
                  setEditingNotes('');
                  handleSaveNotes('');
                }}
                disabled={isSavingNotes}
              >
                Clear
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setNotesPopover(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => handleSaveNotes()}
              disabled={isSavingNotes}
            >
              {isSavingNotes ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectorView;
